import { useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import ReactSignatureCanvas from 'react-signature-canvas';

type Props = {
  canvasRef: React.RefObject<ReactSignatureCanvas>;
  isConfirmed: boolean;
  confirmedDataUrl: string | null;
  onBegin?: () => void;
  onEnd?: () => void;
  disabled?: boolean;
};

export const SignatureCanvas = ({
  canvasRef,
  isConfirmed,
  confirmedDataUrl,
  onBegin,
  onEnd,
  disabled = false
}: Props) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && containerRef.current && !isConfirmed) {
        const canvas = canvasRef.current.getCanvas();
        const container = containerRef.current;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);

        canvas.width = container.offsetWidth * ratio;
        canvas.height = container.offsetHeight * ratio;
        canvas.getContext('2d')?.scale(ratio, ratio);

        canvas.style.width = `${container.offsetWidth}px`;
        canvas.style.height = `${container.offsetHeight}px`;
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [canvasRef, isConfirmed]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        minHeight: 150,
        height: 150,
        border: `2px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        position: 'relative',
        overflow: 'hidden',
        touchAction: 'none'
      }}
    >
      {isConfirmed && confirmedDataUrl ? (
        <Box
          component="img"
          src={confirmedDataUrl}
          alt="Signature"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
            opacity: 1
          }}
        />
      ) : disabled ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.action.disabledBackground
          }}
        />
      ) : (
        <ReactSignatureCanvas
          ref={canvasRef}
          penColor={theme.palette.mode === 'dark' ? '#ffffff' : '#000000'}
          backgroundColor="transparent"
          canvasProps={{
            style: {
              width: '100%',
              height: '100%',
              touchAction: 'none'
            }
          }}
          onBegin={onBegin}
          onEnd={onEnd}
        />
      )}
    </Box>
  );
};
